import { Video, Shield, FileText, Sparkles, AlertTriangle, XCircle, Zap, BarChart3 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

/**
 * Login Page
 * Kullanıcı giriş sayfası
 */
export function LoginPage() {
  const { login, isLoading, authError, errorMessage, clearError } = useAuth();

  const features = [
    {
      icon: <FileText size={24} className="text-emerald-400" />,
      title: 'Otomatik Transkript',
      description: 'Toplantı transkriptlerinizi otomatik olarak alın',
    },
    {
      icon: <Sparkles size={24} className="text-purple-400" />,
      title: 'AI Destekli Analiz',
      description: 'Özet, önemli noktalar ve aksiyon maddeleri',
    },
    {
      icon: <BarChart3 size={24} className="text-blue-400" />,
      title: 'Detaylı Raporlar',
      description: 'PDF ve Word formatında dışa aktarma',
    },
    {
      icon: <Shield size={24} className="text-orange-400" />,
      title: 'Güvenli Erişim',
      description: 'Sadece kendi toplantılarınıza erişim',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-indigo-500/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      
      <div className="max-w-5xl w-full relative z-10">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/20">
          <div className="grid md:grid-cols-2">
            {/* Left Side - Info */}
            <div className="p-8 md:p-12 text-white">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/50">
                  <Video size={28} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">Teams Transcript</h1>
                  <p className="text-white/60 text-sm">Manager</p>
                </div>
              </div>

              <h2 className="text-3xl font-bold mb-4 leading-tight">
                Toplantı transkriptlerinizi{' '}
                <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                  akıllıca
                </span>{' '}
                yönetin
              </h2>
              <p className="text-white/70 mb-10 text-lg">
                Microsoft Teams toplantılarınızın transkriptlerini görüntüleyin, 
                AI ile analiz edin ve raporlarınızı kolayca oluşturun.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div 
                    key={index} 
                    className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                  >
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center mb-3">
                      {feature.icon}
                    </div>
                    <h3 className="font-semibold text-sm mb-1">{feature.title}</h3>
                    <p className="text-xs text-white/50">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Login */}
            <div className="bg-white p-8 md:p-12 flex flex-col items-center justify-center">
              <div className="w-full max-w-sm">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-indigo-500/30">
                    <Zap size={32} className="text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Hoş Geldiniz</h2>
                  <p className="text-slate-500">
                    Devam etmek için şirket hesabınızla giriş yapın
                  </p>
                </div>

                {/* Hata Mesajı - Yetkisiz Erişim */}
                {authError === 'unauthorized' && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <XCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-medium text-red-800">Erişim Yetkiniz Yok</h4>
                        <p className="text-sm text-red-700 mt-1">
                          Bu uygulama şu anda sınırlı sayıda kullanıcı tarafından test edilmektedir.
                          Erişim için IT departmanınızla iletişime geçin.
                        </p>
                        <a 
                          href="mailto:it-support@company.com?subject=Teams%20Transcript%20Manager%20Erişim%20Talebi"
                          className="inline-block mt-2 text-sm text-red-800 underline hover:no-underline"
                        >
                          IT Desteğe Yazın →
                        </a>
                      </div>
                      <button onClick={clearError} className="text-red-400 hover:text-red-600">
                        <XCircle size={16} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Hata Mesajı - Diğer Hatalar */}
                {authError && authError !== 'unauthorized' && (
                  <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-medium text-amber-800">Giriş Yapılamadı</h4>
                        <p className="text-sm text-amber-700 mt-1">
                          {errorMessage || 'Bir hata oluştu. Lütfen tekrar deneyin.'}
                        </p>
                      </div>
                      <button onClick={clearError} className="text-amber-500 hover:text-amber-700">
                        <XCircle size={16} />
                      </button>
                    </div>
                  </div>
                )}

                <button
                  onClick={login}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30 font-medium"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 0H0V10H10V0Z" fill="#F25022"/>
                        <path d="M21 0H11V10H21V0Z" fill="#7FBA00"/>
                        <path d="M10 11H0V21H10V11Z" fill="#00A4EF"/>
                        <path d="M21 11H11V21H21V11Z" fill="#FFB900"/>
                      </svg>
                      <span>Microsoft ile Giriş Yap</span>
                    </>
                  )}
                </button>

                <div className="mt-6 flex items-center gap-3">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400">veya</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>

                <p className="text-center text-sm text-slate-400 mt-6">
                  Giriş yaparak{' '}
                  <a href="#" className="text-indigo-600 hover:underline">kullanım koşullarını</a>
                  {' '}kabul etmiş olursunuz.
                </p>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-sm text-white/40 mt-6">
          © 2026 Teams Transcript Manager • AI Destekli Toplantı Analizi
        </p>
      </div>
    </div>
  );
}
